import type React from 'react'
import {useState} from 'react'
import {DragAndDropMoveModal} from './DragAndDropMoveModal'
import {useSortableItem} from '../hooks/use-sortable-item'
import {useDragAndDrop} from '../hooks/use-drag-and-drop'

export interface MoveModalTriggerProps<T> {
  /**
   * The clickable component that will trigger the move modal to open
   */
  Component: T
  /**
   * The onClick handler for the component
   */
  onClick?: React.MouseEventHandler<T>
}

/**
 * A trigger component that opens the move modal when clicked.
 *
 * @param props MoveModalTriggerProps
 */
export const MoveModalTrigger = <T extends React.ElementType>({
  Component,
  onClick,
  ...props
}: MoveModalTriggerProps<T> & React.ComponentProps<T>) => {
  const [openMoveModal, setOpenMoveModal] = useState(false)
  const {title, index} = useSortableItem()
  const {items} = useDragAndDrop()

  const openDialog = (e: React.MouseEventHandler<T>) => {
    setOpenMoveModal(true)
    onClick?.(e)
  }

  if (items.length === 1) return null

  return (
    <>
      {openMoveModal && <DragAndDropMoveModal title={title} currentIndex={index} closeDialog={setOpenMoveModal} />}
      <Component {...props} onClick={openDialog} />
    </>
  )
}

try{ MoveModalTrigger.displayName ||= 'MoveModalTrigger' } catch {}